import { useState, useCallback, useMemo } from 'react';
import { useCurrentSite } from '@hotjar/site';
import { useNotify } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';
import { useDeleteSurveyWidgetMutation } from '../../../domain/Surveys/query';

export const useDeleteActions = (
  onSuccess?: () => void,
): {
  beginDelete: (surveyId: number) => void;
  isDeleteConfirmed: boolean;
  isDeleteInProgress: boolean;
  continueDelete: () => Promise<void>;
  cancelDelete: () => void;
} => {
  const deleteSurveyMutation = useDeleteSurveyWidgetMutation();

  const { data: currentSite } = useCurrentSite();
  const { showToast } = useNotify();
  const { t } = useTranslation('feedback');

  const onDelete = useCallback(
    async (surveyId: number) => {
      if (currentSite === undefined) return;
      try {
        await deleteSurveyMutation.mutateAsync({ siteId: currentSite.id, surveyId });
        showToast({ kind: 'success', text: t('survey-list.notification.delete-success') });
        onSuccess?.();
      } catch {
        showToast({ kind: 'danger', text: t('survey-list.notification.delete-error') });
      }
    },
    [currentSite, showToast, deleteSurveyMutation, t, onSuccess],
  );

  const [isDeleteConfirmed, setIsDeleteConfirmed] = useState(false);
  const [pendingId, setPendingId] = useState<number>();

  const beginDelete = useCallback((surveyId: number) => {
    setPendingId(surveyId);
    setIsDeleteConfirmed(false);
  }, []);

  const cancelDelete = useCallback(() => {
    setPendingId(undefined);
    setIsDeleteConfirmed(false);
  }, []);

  const continueDelete = useCallback(async () => {
    if (pendingId === undefined) throw new Error('Delete is not in progress');
    setIsDeleteConfirmed(true);
    try {
      await onDelete(pendingId);
    } finally {
      setPendingId(undefined);
      setIsDeleteConfirmed(false);
    }
  }, [onDelete, pendingId]);

  return useMemo(
    () => ({
      beginDelete,
      isDeleteConfirmed,
      isDeleteInProgress: pendingId !== undefined,
      continueDelete,
      cancelDelete,
    }),
    [beginDelete, isDeleteConfirmed, pendingId, continueDelete, cancelDelete],
  );
};
